import type { DocVariableSnapshot } from "@/lib/document-builder/versions/snapshotTypes";
import { docVariablePlaceholder } from "@/lib/document-builder/variables/variableKey";

type Props = {
  variables: DocVariableSnapshot[];
  onChange: (variables: DocVariableSnapshot[]) => void;
  disabled?: boolean;
};

export function DocumentVariablesEditor({ variables, onChange, disabled }: Props) {
  function updateRow(index: number, patch: Partial<DocVariableSnapshot>) {
    onChange(variables.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function addRow() {
    onChange([
      ...variables,
      { key: "", label: "", default_value: "", required: false },
    ]);
  }

  function removeRow(index: number) {
    onChange(variables.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-600 dark:text-zinc-400">
        Define template variables for export-time fill. Reference them in paragraph or rich text as{" "}
        <code className="rounded bg-gray-100 px-1 dark:bg-zinc-800">{docVariablePlaceholder("CustomerName")}</code>
        , or bind a text field block to a variable key.
      </p>

      {variables.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No variables yet.</p>
      ) : (
        <ul className="space-y-2">
          {variables.map((row, index) => (
            <li
              key={index}
              className="grid gap-2 rounded-lg border border-gray-200 p-3 dark:border-zinc-700 sm:grid-cols-[1fr_1fr_1fr_auto_auto]"
            >
              <label className="block text-xs">
                <span className="text-gray-500">Key</span>
                <input
                  type="text"
                  disabled={disabled}
                  value={row.key}
                  onChange={(e) => updateRow(index, { key: e.target.value })}
                  placeholder="CustomerName"
                  className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 font-mono text-sm dark:border-zinc-600 dark:bg-zinc-900"
                />
              </label>
              <label className="block text-xs">
                <span className="text-gray-500">Label</span>
                <input
                  type="text"
                  disabled={disabled}
                  value={row.label}
                  onChange={(e) => updateRow(index, { label: e.target.value })}
                  placeholder="Customer name"
                  className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                />
              </label>
              <label className="block text-xs">
                <span className="text-gray-500">Default</span>
                <input
                  type="text"
                  disabled={disabled}
                  value={row.default_value}
                  onChange={(e) => updateRow(index, { default_value: e.target.value })}
                  className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                />
              </label>
              <label className="flex items-end gap-2 pb-1 text-xs">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={row.required}
                  onChange={(e) => updateRow(index, { required: e.target.checked })}
                />
                <span className="text-gray-600 dark:text-zinc-300">Required</span>
              </label>
              <button
                type="button"
                disabled={disabled}
                onClick={() => removeRow(index)}
                className="self-end rounded border border-gray-300 px-2 py-1 text-xs dark:border-zinc-600"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        disabled={disabled}
        onClick={addRow}
        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600"
      >
        Add variable
      </button>
    </div>
  );
}
